import { signal, inject } from "@angular/core";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { ErrorResponse } from "@bitwarden/common/models/response/error.response";

import { StorageView } from "@bitwarden/web-vault/app/models/view/storage/storage.view";
import {
  ExternalCollection,
  FetchParams,
} from "@bitwarden/web-vault/app/services/store/external.collection.abstraction";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { StateService } from "@bitwarden/web-vault/app/core";
import { UsersConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import {
  SubscriptionResponse,
  SubscriptionView,
  toView,
} from "@bitwarden/web-vault/app/models/view/subscription/subscription.view";

type ExternalUserView = {
  storage?: StorageView;
  subscriptions?: SubscriptionView[];
};

interface SubscriptionFetchParams extends FetchParams {
  userId: string;
}

type StorageResponse = {
  size_limit: number;
  usage: {
    s3_in_use: number; // cabinet storage usage in Bytes
    db_in_use: number; // vault storage usage in kB
  } | null;
};

type UserResponse<T = any> = {
  id: string;
  realm: string;
  data: T;
};

type RealmType = "VAULT" | "GLOSS" | "SUBSCRIPTION";

const USERS_CONFIG = process.env.USERS as unknown as UsersConfigType;

function createRealmTypeGuard<T>(type: RealmType) {
  return (data: UserResponse): data is UserResponse<T> => {
    return data.realm.startsWith(type);
  };
}

// This is a type guard for StorageResponse
const isStorageResponse = createRealmTypeGuard<StorageResponse>("VAULT");

// This is a type guard for SubscriptionResponse
const isSubscriptionResponse = createRealmTypeGuard<SubscriptionResponse>("SUBSCRIPTION");

// This is the default storage view used when no data is available
const DEFAULT_STORAGE = new StorageView({
  limit: 100,
  s3InUsed: 0,
  vaultInUsed: 0,
});

/** How long after a failed startup fetch it tries once more (#6034). */
export const USER_FETCH_RETRY_MS = 10_000;

export class ExternalUserCollection extends ExternalCollection<
  ExternalUserView,
  SubscriptionFetchParams
> {
  private legacyAppState = inject(StateService);
  private dataRepositoryService = inject(DataRepositoryService);
  private log = inject(LogService);
  // storage usage
  private _usage = signal<StorageView>(DEFAULT_STORAGE);
  readonly usage = this._usage.asReadonly();
  /**
   * Subscription history, newest end date first. null until a fetch succeeds (not loaded yet, or
   * the fetch failed); [] once loaded means the user has none (#6034).
   */
  private _subscriptions = signal<SubscriptionView[] | null>(null);
  readonly subscriptions = this._subscriptions.asReadonly();

  // The fetch in flight, so callers share it rather than starting another.
  private loading: Promise<void> | null = null;
  // Set by triggerChanges(): until the session's first load has started (there may be no user id
  // yet), loadIfUnknown() leaves it to that.
  private started = false;
  // The one retry after a failed startup fetch; clear() cancels it.
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  // Bumped by clear(): a fetch started for the previous user doesn't touch this session.
  private generation = 0;

  clear(): void {
    this.generation++;
    this.started = false;
    this.loading = null;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this._usage.set(DEFAULT_STORAGE);
    this._subscriptions.set(null);
  }

  /**
   * Never rejects, so UserStoreService.initialize() can start it without awaiting it (#6401): a
   * failed GET /users/:id used to stop Tier 1, leaving the store-initialisation overlay up (#6034).
   * A failure is logged, the history stays null (unknown), and it tries once more after
   * USER_FETCH_RETRY_MS; loadIfUnknown() lets a screen that needs the history ask again.
   */
  async triggerChanges(): Promise<void> {
    this.started = true;
    // An earlier run's retry: this run's own failure, if any, schedules a full USER_FETCH_RETRY_MS.
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const generation = this.generation;
    await this.load();
    if (this._subscriptions() === null && generation === this.generation) {
      // Through loadIfUnknown(): if something asked and the history loaded meanwhile, it's a no-op.
      this.retryTimer ??= setTimeout(() => {
        this.retryTimer = null;
        void this.loadIfUnknown();
      }, USER_FETCH_RETRY_MS);
    }
  }

  /**
   * Fetches again if the history isn't known (the fetch failed), and waits for it; shares a fetch
   * already in flight. Only a user action calls it (opening Settings), so it needs no rate limit
   * (#6400). Before the session's first load (triggerChanges) it does nothing.
   */
  async loadIfUnknown(): Promise<void> {
    if (this.started && this._subscriptions() === null) {
      await this.load();
    }
  }

  private load(): Promise<void> {
    if (!this.loading) {
      const loading = this.fetchAndSet(this.generation).finally(() => {
        if (this.loading === loading) {
          this.loading = null;
        }
      });
      this.loading = loading;
    }
    return this.loading;
  }

  // All of it in the try: triggerChanges() must not reject, whatever fails.
  private async fetchAndSet(generation: number): Promise<void> {
    try {
      const userId = await this.legacyAppState.getUserId();
      const data = await this.fetch({ userId: userId });
      if (generation !== this.generation) {
        return;
      }
      if (data.storage) {
        this._usage.set(data.storage);
      }
      // Descending. An end date that isn't one (the default ironfly subscription's is "") sorts
      // last: as NaN it made the comparison NaN, and the order whatever the engine left it.
      const endTime = (s: SubscriptionView) => {
        const time = s.endDate.getTime();
        return Number.isNaN(time) ? -Infinity : time;
      };
      const sortedSubscriptions = [...data.subscriptions].sort((a, b) => {
        const [timeA, timeB] = [endTime(a), endTime(b)];
        return timeA === timeB ? 0 : timeB > timeA ? 1 : -1;
      });
      // Set even when empty: [] is "loaded, none", unlike null.
      this._subscriptions.set(sortedSubscriptions);
    } catch (e) {
      if (generation === this.generation) {
        // DataRepositoryService.send rejects with null (401/403), an ErrorResponse or a string; an
        // Error is a bug in handling the response, and its stack says where.
        const reason =
          e instanceof Error
            ? (e.stack ?? String(e))
            : `${(e as ErrorResponse)?.statusCode ?? ""} ${e}`.trim();
        this.log.error(`ExternalUserCollection: fetching the user failed: ${reason}`);
      }
    }
  }

  async fetch(params: SubscriptionFetchParams): Promise<ExternalUserView> {
    const path = `/${params.userId}`;
    const apiUrl = `${USERS_CONFIG.url}/${USERS_CONFIG.apiStage}${USERS_CONFIG.endpoint.get_users}`;
    const res: UserResponse[] | null = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      apiUrl,
    );
    return this.mapToView(res);
  }

  protected mapToView(data: UserResponse[] | null): ExternalUserView {
    // No rows at all (a 204, or a 200 with []) isn't a loaded history: every user has at least a
    // VAULT row. Throwing keeps the history null (unknown) rather than [] (loaded, none) (#6034).
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error("GET /users/:id returned no rows");
    }
    // TODO: Also can generate user profile when key is "GLOSS"
    // then we can avoid calling apiService.getProfile() in plans.component.ts
    // which will be triggered every time the user click Profile in Settings page
    return data.reduce(
      (acc, d) => {
        if (isStorageResponse(d)) {
          acc.storage = this.mapToStorageView(d.data);
        } else if (isSubscriptionResponse(d)) {
          acc.subscriptions.push(this.mapToSubscriptionView(d.data));
        }
        return acc;
      },
      { subscriptions: [] } as ExternalUserView,
    );
  }

  private mapToStorageView(data: StorageResponse): StorageView {
    return new StorageView({
      limit: data.size_limit,
      s3InUsed: (data.usage?.s3_in_use ?? 0) / 1024 / 1024, // Convert Bytes to MB
      vaultInUsed: (data.usage?.db_in_use ?? 0) / 1024, // Convert kB to MB
    });
  }

  private mapToSubscriptionView(data: SubscriptionResponse): SubscriptionView {
    return toView(data);
  }
}
